import { isBlankContentValue, isUnreachableFieldPath } from './internal';
import { walkTranslatableUnits } from './walk';
import {
  H5PParams,
  H5PSemantics,
  ProgressCount,
  TranslatableField,
  TranslationMap,
  TranslationProgress,
  TRANSLATABLE_FIELD_TYPES,
  WalkerOptions,
} from './types';

/**
 * Count translation progress of a content against a translation map.
 *
 * Counting rules:
 * - A field counts toward `total` only when its SOURCE value holds real
 *   content (`isBlankContentValue` — html stripped of tags is blank, media
 *   objects need a path/url) — an empty source needs no translation (merge
 *   falls back to the empty source either way). This subsumes the
 *   `translatableCondition: 'sourceNonEmpty'` rule for conditional fields.
 *   The SAME rule applies to the module sum and to every `perItem` entry.
 * - Fields inside a group the editor never renders (`isUnreachableFieldPath`)
 *   are skipped: nobody can reach an input for them, so counting them would
 *   put 100% permanently out of reach. Counting only — extraction and merge
 *   still handle those keys unchanged.
 * - A counted field is `translated` when the map holds a non-empty,
 *   type-compatible value under its key. Fields that cannot be keyed yet
 *   (legacy items without minted ids) count as untranslated.
 * - `perItem` aggregates hierarchically: a field is attributed to every keyed
 *   item on its ancestor chain (so a QuestionSet question's entry includes
 *   its answer options and embedded sub-content). Hard-excluded items
 *   (Drag & Drop) get an explicit `0/0` entry.
 */
export const countProgress = (
  semantics: H5PSemantics,
  params: H5PParams,
  map: TranslationMap | undefined | null,
  options?: WalkerOptions,
): TranslationProgress => {
  const { fields, excludedItems } = walkTranslatableUnits(
    semantics,
    params,
    options,
  );
  const perItem: Record<string, { translated: number; total: number }> = {};
  let translated = 0;
  let total = 0;

  const bump = (key: string, isTranslated: boolean): void => {
    const entry = (perItem[key] = perItem[key] ?? { translated: 0, total: 0 });
    entry.total++;
    if (isTranslated) {
      entry.translated++;
    }
  };

  for (const field of fields) {
    if (
      isBlankContentValue(field.sourceValue) ||
      isUnreachableFieldPath(field.pathSegments)
    ) {
      continue;
    }
    const isTranslated = isFieldTranslated(field, map);
    total++;
    if (isTranslated) {
      translated++;
    }
    for (const key of field.keyChain) {
      bump(key, isTranslated);
    }
  }

  for (const excluded of excludedItems) {
    perItem[excluded.itemKey] = perItem[excluded.itemKey] ?? {
      translated: 0,
      total: 0,
    };
  }

  return { translated, total, perItem };
};

const isFieldTranslated = (
  field: TranslatableField,
  map: TranslationMap | undefined | null,
): boolean => {
  if (!map || field.mapKey === null) {
    return false;
  }
  const value = map[field.mapKey];
  if (isBlankContentValue(value)) {
    return false;
  }
  const expectsString =
    field.type === TRANSLATABLE_FIELD_TYPES.TEXT ||
    field.type === TRANSLATABLE_FIELD_TYPES.HTML;
  return expectsString ? typeof value === 'string' : typeof value !== 'string';
};

/** Convenience: percentage helper for UI chips (0-100, 100 when 0/0). */
export const progressPercentage = (count: ProgressCount): number =>
  count.total === 0 ? 100 : Math.round((count.translated / count.total) * 100);
